import { useState, useCallback, useEffect } from 'react';
import { getSummaryHotEventTimeLine } from '@op/services';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { arrayToObj, getTimeSpaceArray } from '@op/utils';
import { formatDateFunc } from '@op/utils/time';
import type { IDiscussionItem } from '../components/HotEventPanel/types';

export type TDateType = 'week' | 'month' | 'all';

/**
 * Hot Event Timeline Hook（Event Tracking 趋势图数据）
 * 使用 /summary/hot_event/timeline 接口
 */
export const useHotEventTimeline = (
    item: IDiscussionItem | null,
    filterParams: Record<string, any> | undefined,
) => {
    const [eventValue, setEventValue] = useState<TDateType>('week');
    const [lineData, setLineData] = useState<{ x: any[]; y: any[]; empty: boolean }>({
        x: [],
        y: [],
        empty: false,
    });
    const { loading, wrapperRequestTile } = useCancelRequestHook({
        clear: () => setLineData({ x: [], y: [], empty: true }),
    });
 
    const getTimeline = useCallback(
        (dateType: TDateType) => {
            if (!item?.cluster_id || !filterParams) return Promise.resolve(); 
            return wrapperRequestTile(getSummaryHotEventTimeLine, {
                ...filterParams,
                cluster_id: item.cluster_id,
                timeline_date_type: dateType,
            }).then((data: any) => {
                const { timeline_of_start_time, timeline_of_end_time, timeline } = data || {};
                const dateFunc = formatDateFunc('daily');
                const timeList = getTimeSpaceArray(timeline_of_start_time, timeline_of_end_time);
                const timelineMap = arrayToObj(timeline, (t: any) => dateFunc(t.time));

                const y: any = {
                    mentions: { data: [], name: 'Mentions', color: '#5086F3' },
                    rank: { data: [], name: 'Rank', color: '#69D19C' },
                };
                const x: any = [];

                timeList.forEach((time: string) => {
                    const key = dateFunc(time);
                    const { mentions, rank } = timelineMap?.[key] || {};
                    x.push(time);
                    y.mentions.data.push(mentions || 0);
                    y.rank.data.push(rank || 0);
                });

                setLineData({
                    x,
                    y: Object.values(y),
                    empty: (timeline || [])?.length === 0,
                });
                return data;
            });
        },
        [filterParams, item?.cluster_id],
    );

    // 自动请求
    useEffect(() => {
        if (!item?.cluster_id || !filterParams) {
            setLineData({ x: [], y: [], empty: false });
            return;
        }
        getTimeline(eventValue);
    }, [item?.cluster_id, filterParams]);

    return { eventValue, lineData, loading, setEventValue, getTimeline } as const;
};
